import React from 'react';
import { motion } from 'framer-motion';
import BorderGlow from './BorderGlow';

import { useTheme } from './ThemeProvider';
import { useLanguage } from './LanguageProvider';
import portfolioItems from '../data/portfolio.json';

const projects = portfolioItems;
const GLOW_COLORS = ['#FF9500', '#ff6b00', '#ffcc44'];

function localized(field, language) {
    if (field && typeof field === 'object') return field[language] ?? field.en;
    return field;
}

export default function PortfolioPage() {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';
    const bgColor = isDark ? '#0a0a0a' : '#ffffff';

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#050505] pt-32 pb-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Header Area */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8">
                    <div>
                        <motion.h1 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            className="text-6xl md:text-7xl font-extrabold tracking-tight text-gray-900 dark:text-white"
                        >
                            Selected<br />
                            <span className="text-gray-400 dark:text-gray-600">Work</span>
                        </motion.h1>
                    </div>
                    
                    <motion.div 
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                        className="flex flex-col items-end text-right gap-4"
                    >
                        <div>
                            <div className="text-3xl font-black text-gray-900 dark:text-white leading-none">4</div>
                            <div className="text-sm font-medium text-gray-500">Projects delivered</div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-gray-900 dark:text-white leading-none">3</div>
                            <div className="text-sm font-medium text-gray-500">Industries served</div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-gray-900 dark:text-white leading-none">100%</div>
                            <div className="text-sm font-medium text-gray-500">Client satisfaction</div>
                        </div>
                    </motion.div>
                </div>
                
                {/* Separator line */}
                <motion.div 
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                    className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF9500] to-transparent mb-24 origin-left"
                />

                {/* Alternating Project Rows */}
                <div className="space-y-32">
                    {projects.map((project, index) => {
                        const isEven = index % 2 !== 0; // index 1, 3 etc. -> flex-row-reverse

                        return (
                            <motion.div 
                                key={project.title}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                className={`flex flex-col lg:flex-row gap-12 lg:gap-24 items-center ${isEven ? 'lg:flex-row-reverse' : ''}`}
                            >
                                {/* Text Content Container */}
                                <div className="w-full lg:w-1/2 flex flex-col items-start relative">
                                    {/* Large Background Number */}
                                    <div className="absolute -top-16 -left-8 text-[150px] font-black text-gray-100 dark:text-[#111] opacity-50 select-none z-0 tracking-tighter pointer-events-none">
                                        {(index + 1).toString().padStart(2, '0')}
                                    </div>
                                    
                                    <div className="relative z-10 w-full">
                                        <div className="flex items-center gap-4 mb-4">
                                            <span className="text-xs font-extrabold uppercase tracking-widest text-[#FF9500]">
                                                {localized(project.tag, language)}
                                            </span>
                                            <div className="h-[1px] flex-1 bg-gray-200 dark:bg-[#222]" />
                                        </div>
                                        
                                        <h2 className="text-4xl sm:text-5xl font-bold text-[#FF9500] mb-6 tracking-tight leading-tight">
                                            {project.title}
                                        </h2>
                                        
                                        <p className="text-base sm:text-lg text-gray-500 dark:text-gray-400 mb-8 leading-relaxed max-w-lg">
                                            {localized(project.description, language)}
                                        </p>
                                        
                                        {project.technologies && (
                                            <div className="flex flex-wrap gap-2 mb-10">
                                                {project.technologies.map(tech => (
                                                    <span key={tech} className="px-4 py-1.5 bg-gray-100 dark:bg-[#151515] text-gray-600 dark:text-gray-400 rounded-full text-xs font-semibold shadow-sm border border-gray-200 dark:border-white/5">
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {project.stats && (
                                            <div className="flex items-end gap-12 mb-10">
                                                <div>
                                                    <div className="text-3xl font-black text-gray-900 dark:text-white mb-1">
                                                        {project.stats.split(' ')[0]}
                                                    </div>
                                                    <div className="text-xs font-semibold text-gray-400 tracking-wide uppercase">
                                                        {project.stats.split(' ').slice(1).join(' ')}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex gap-6 items-center">
                                            {project.caseStudy && project.caseStudy !== '#' && (
                                                <a href={project.caseStudy} className="text-sm font-bold text-gray-900 dark:text-white hover:text-[#FF9500] dark:hover:text-[#FF9500] transition-colors flex items-center gap-2 group">
                                                    Read case study
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 transition-transform group-hover:translate-x-1"><path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" /></svg>
                                                </a>
                                            )}
                                            {project.link && (
                                                <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors flex items-center gap-1 group">
                                                    Visit Website
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Image / macOS Window Container */}
                                <div className="w-full lg:w-1/2">
                                    <BorderGlow
                                        edgeSensitivity={22}
                                        glowColor="30 100 60"
                                        backgroundColor={bgColor}
                                        borderRadius={24}
                                        glowRadius={40}
                                        glowIntensity={0.8}
                                        coneSpread={30}
                                        colors={GLOW_COLORS}
                                        className="w-full shadow-2xl"
                                    >
                                        <div className="overflow-hidden rounded-[23px] bg-[#1a1a1a] shadow-2xl flex flex-col border border-white/5 w-full">
                                            {/* macOS Window Header */}
                                            <div className="h-10 bg-[#151515] border-b border-[#2a2a2a] flex items-center px-4 gap-2 relative">
                                                <div className="flex gap-2 z-10">
                                                    <div className="w-3 h-3 rounded-full bg-[#ff5f56]"></div>
                                                    <div className="w-3 h-3 rounded-full bg-[#ffbd2e]"></div>
                                                    <div className="w-3 h-3 rounded-full bg-[#27c93f]"></div>
                                                </div>
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                    <div className="bg-[#0a0a0a] border border-[#2a2a2a] px-16 sm:px-32 py-1 rounded-md text-[10px] text-gray-400 font-medium tracking-wide truncate max-w-[50%]">
                                                        {new URL(project.link || 'https://gridlabsprod.com').hostname}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Browser viewport */}
                                            <div className="relative aspect-video w-full bg-[#0a0a0a] group/img overflow-hidden">
                                                <img
                                                    src={project.image}
                                                    alt={project.title}
                                                    className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover/img:scale-105 group-hover/img:-translate-y-2"
                                                />
                                            </div>
                                        </div>
                                    </BorderGlow>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
